import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, InputNumber, Spin, Typography, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, put } from '../../api/request';
import '../User/UserList.css';
import ReaderAdvertisementSettingsCard from './ReaderAdvertisementSettingsCard';

const { Paragraph, Text } = Typography;

interface GenerationSettings {
  max_chapters: number;
  default_chapters: number;
  default_words_per_chapter: number;
  publish_min_score: number;
  updated_at?: string | null;
}

interface GenerationSettingsForm {
  max_chapters: number;
  default_chapters: number;
  default_words_per_chapter: number;
  publish_min_score: number;
}

const Settings: React.FC = () => {
  const [form] = Form.useForm<GenerationSettingsForm>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<GenerationSettings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const result = await get<GenerationSettings>(API_ENDPOINTS.system_app.generationSettings);
        setSettings(result);
        form.setFieldsValue(result);
      } catch (error) {
        message.error(error instanceof Error ? error.message : '小说生成设置加载失败');
      } finally {
        setLoading(false);
      }
    };
    void loadSettings();
  }, [form]);

  const onFinish = async (values: GenerationSettingsForm) => {
    setSaving(true);
    try {
      const result = await put<GenerationSettings>(
        API_ENDPOINTS.system_app.generationSettings,
        values,
      );
      setSettings(result);
      form.setFieldsValue(result);
      message.success('小说生成设置已保存');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="user-list-container">
      <h1 className="page-title">系统设置</h1>

      <Card title="小说生成设置" bordered={false} style={{ maxWidth: 760 }}>
        <Spin spinning={loading}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            message="统一控制新建小说的默认章节数、单章字数和允许上限"
            description="系统默认按 50 章规划全书、每章约 1000 字；已有小说保持原设定，新建后仍可在作品设定页单独调整。"
          />
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ max_chapters: 200, default_chapters: 50, default_words_per_chapter: 1000, publish_min_score: 80 }}
          >
            <Form.Item
              label="新建小说默认章节数"
              name="default_chapters"
              dependencies={['max_chapters']}
              extra="没有个人偏好时，新项目将使用这个章节数；系统初始值为 50 章。"
              rules={[
                { required: true, message: '请输入默认章节数' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (Number(value) >= 3 && Number(value) <= Number(getFieldValue('max_chapters'))) return Promise.resolve();
                    return Promise.reject(new Error('默认章节数必须在 3 章与最大章节数之间'));
                  },
                }),
              ]}
            >
              <InputNumber min={3} max={1000} precision={0} style={{ width: 220 }} addonAfter="章" />
            </Form.Item>
            <Form.Item
              label="新建小说默认单章字数"
              name="default_words_per_chapter"
              extra="允许设置 500–5000 字；1000 字偏短、生成更快，热门网文章节通常约 2000 字。"
              rules={[
                { required: true, message: '请输入默认单章字数' },
                { type: 'number', min: 500, max: 5000, message: '请输入 500–5000 之间的整数' },
              ]}
            >
              <InputNumber min={500} max={5000} step={100} precision={0} style={{ width: 220 }} addonAfter="字/章" />
            </Form.Item>
            <Form.Item
              label="允许发布的最低审稿分"
              name="publish_min_score"
              extra="默认 80 分；修改后立即影响用户端审稿状态和后端发布校验。"
              rules={[
                { required: true, message: '请输入发布最低分' },
                { type: 'number', min: 0, max: 100, message: '请输入 0–100 之间的整数' },
              ]}
            >
              <InputNumber min={0} max={100} precision={0} style={{ width: 220 }} addonAfter="分" />
            </Form.Item>
            <Form.Item
              label="单本小说最大章节数"
              name="max_chapters"
              extra="允许设置 3–1000 章；修改后立即影响新建项目、设定、大纲和章节生成。"
              rules={[
                { required: true, message: '请输入最大章节数' },
                { type: 'number', min: 3, max: 1000, message: '请输入 3–1000 之间的整数' },
              ]}
            >
              <InputNumber min={3} max={1000} precision={0} style={{ width: 220 }} addonAfter="章" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                保存设置
              </Button>
            </Form.Item>
          </Form>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            当前默认：<Text strong>{settings?.default_chapters ?? 50} 章</Text>，
            <Text strong>{settings?.default_words_per_chapter ?? 1000} 字/章</Text>，
            发布最低分：<Text strong>{settings?.publish_min_score ?? 80} 分</Text>，
            最大允许：<Text strong>{settings?.max_chapters ?? 200} 章</Text>
            {settings?.updated_at ? `，最后更新于 ${new Date(settings.updated_at).toLocaleString('zh-CN', { hour12: false })}` : '（系统默认值）'}
          </Paragraph>
        </Spin>
      </Card>
      <ReaderAdvertisementSettingsCard />
    </div>
  );
};

export default Settings;
